// src/components/gps/GPSLoadMonitoring.js
import React, { useState, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Table, Badge, Button, Modal, FormField, Input, Select, SearchInput, ResponsiveGrid, ConfirmDialog } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';

function daysBetween(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr); target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

function statusFor(daysLeft) {
  if (daysLeft == null) return { label: 'Unknown', color: '#6b7280' };
  if (daysLeft < 0) return { label: 'Expired', color: '#ef4444' };
  if (daysLeft === 0) return { label: 'Expires Today', color: '#ef4444' };
  if (daysLeft <= 3) return { label: `${daysLeft}d left`, color: '#ef4444' };
  if (daysLeft <= 7) return { label: `${daysLeft}d left`, color: '#f59e0b' };
  return { label: `${daysLeft}d left`, color: '#10b981' };
}

const emptyForm = () => ({
  gpsDeviceId: '', dateLoaded: new Date().toISOString().split('T')[0],
  expirationDate: '', amount: '', notes: '',
});

export default function GPSLoadMonitoring() {
  const { gpsDevices, gpsLoads, addGPSLoad, updateGPSLoad, deleteGPSLoad, trucks } = useApp();
  const { isMobile } = useResponsive();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editLoad, setEditLoad] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const enriched = useMemo(() => gpsLoads.map(l => {
    const daysLeft = daysBetween(l.expirationDate);
    return { ...l, daysLeft, status: statusFor(daysLeft) };
  }).sort((a, b) => (a.daysLeft ?? 999) - (b.daysLeft ?? 999)), [gpsLoads]);

  const filtered = enriched.filter(l => {
    const device = gpsDevices.find(g => g.id === l.gpsDeviceId);
    return device?.deviceId?.toLowerCase().includes(search.toLowerCase())
      || device?.simCard?.includes(search)
      || device?.networkProvider?.toLowerCase().includes(search.toLowerCase())
      || l.amount?.toLowerCase().includes(search.toLowerCase());
  });

  const expiredCount = enriched.filter(l => l.daysLeft != null && l.daysLeft < 0).length;
  const expiringSoonCount = enriched.filter(l => l.daysLeft != null && l.daysLeft >= 0 && l.daysLeft <= 7).length;
  const activeCount = enriched.filter(l => l.daysLeft != null && l.daysLeft > 7).length;

  const openAdd = () => {
    setEditLoad(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (load) => {
    setEditLoad(load);
    setForm({
      gpsDeviceId: load.gpsDeviceId, dateLoaded: load.dateLoaded || '',
      expirationDate: load.expirationDate || '', amount: load.amount || '', notes: load.notes || '',
    });
    setModalOpen(true);
  };

  // Convenience: pick a duration and auto-fill the expiration date from "date loaded"
  const applyDuration = (days) => {
    if (!form.dateLoaded) return;
    const d = new Date(form.dateLoaded);
    d.setDate(d.getDate() + days);
    setForm(f => ({ ...f, expirationDate: d.toISOString().split('T')[0] }));
  };

  const isValid = form.gpsDeviceId && form.dateLoaded && form.expirationDate;

  const handleSave = () => {
    if (!isValid) return;
    if (editLoad) updateGPSLoad(editLoad.id, form);
    else addGPSLoad(form);
    setModalOpen(false);
  };

  const columns = [
    {
      key: 'gpsDeviceId', label: 'GPS Device',
      render: (v) => {
        const device = gpsDevices.find(g => g.id === v);
        const truck = device ? trucks.find(t => t.id === device.assignedTruck) : null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(139,92,246,0.12)', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>📶</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)', fontFamily: 'Syne, sans-serif' }}>{device?.deviceId || 'Unknown device'}</div>
              <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>
                {device?.simCard || 'No SIM on file'}
                {device?.networkProvider && ` (${device.networkProvider})`}
                {truck ? ` · ${truck.plateNumber}` : ''}
              </div>
            </div>
          </div>
        );
      }
    },
    { key: 'dateLoaded', label: 'Date Loaded', render: v => <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{v || '—'}</span> },
    { key: 'expirationDate', label: 'Expires', render: v => <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{v || '—'}</span> },
    {
      key: 'daysLeft', label: 'Days Left',
      render: (v, row) => <Badge color={row.status.color}>{row.status.label}</Badge>
    },
    { key: 'amount', label: 'Load Amount', render: v => <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{v || '—'}</span> },
    {
      key: 'id', label: '',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setConfirmDelete(row); }}>Del</Button>
        </div>
      )
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by device, SIM number, or amount..." />
          </div>
          <Button onClick={openAdd} icon="＋">Add Load Record</Button>
        </div>

        <ResponsiveGrid columns={3} tabletColumns={3} mobileColumns={1} gap={12} style={{ marginBottom: 20 }}>
          <div style={{ background: 'var(--bgInput)', border: '1px solid rgba(16,185,129,0.22)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, color: '#10b981' }}>{activeCount}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 4 }}>Active</div>
          </div>
          <div style={{ background: 'var(--bgInput)', border: '1px solid rgba(245,158,11,0.22)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, color: '#f59e0b' }}>{expiringSoonCount}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 4 }}>Expiring ≤7 Days</div>
          </div>
          <div style={{ background: 'var(--bgInput)', border: '1px solid rgba(239,68,68,0.22)', borderRadius: 10, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 32, color: '#ef4444' }}>{expiredCount}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 4 }}>Expired</div>
          </div>
        </ResponsiveGrid>

        <Table columns={columns} data={filtered} emptyMessage="No load records yet — add one to start tracking expiration." />
      </Card>

      {/* Add / Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editLoad ? 'Edit Load Record' : 'Add Load Record'} width={520}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="GPS Device" required>
            <Select
              value={form.gpsDeviceId}
              onChange={v => setForm(f => ({ ...f, gpsDeviceId: v }))}
              placeholder="Select GPS device"
              options={gpsDevices.map(g => ({ value: g.id, label: `${g.deviceId} — ${g.simCard || 'no SIM on file'}${g.networkProvider ? ` (${g.networkProvider})` : ''}` }))}
            />
          </FormField>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <FormField label="Date Loaded" required>
              <Input type="date" value={form.dateLoaded} onChange={v => setForm(f => ({ ...f, dateLoaded: v }))} />
            </FormField>
            <FormField label="Expiration Date" required>
              <Input type="date" value={form.expirationDate} onChange={v => setForm(f => ({ ...f, expirationDate: v }))} />
            </FormField>
          </div>

          <FormField label="Quick Duration" hint="Fills in the expiration date automatically from the date loaded">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[7, 15, 30, 60, 90].map(d => (
                <Button key={d} size="sm" variant="ghost" onClick={() => applyDuration(d)} disabled={!form.dateLoaded}>{d} days</Button>
              ))}
            </div>
          </FormField>

          <FormField label="Load Amount" hint="Optional — e.g. ₱100, 2GB, Unli Data 30 Days">
            <Input value={form.amount} onChange={v => setForm(f => ({ ...f, amount: v }))} placeholder="e.g. ₱100" />
          </FormField>

          <FormField label="Notes">
            <Input value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} placeholder="Optional notes" />
          </FormField>

          {form.dateLoaded && form.expirationDate && (
            (() => {
              const preview = statusFor(daysBetween(form.expirationDate));
              return (
                <div style={{ padding: '10px 14px', background: preview.color + '11', border: `1px solid ${preview.color}33`, borderRadius: 9, fontSize: 12, color: 'var(--textSecondary)' }}>
                  This load will show as <strong style={{ color: preview.color }}>{preview.label}</strong> as of today.
                </div>
              );
            })()
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!isValid}>{editLoad ? 'Save Changes' : 'Add Record'}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteGPSLoad(confirmDelete.id)}
        title="Delete Load Record"
        message="Delete this load record? This cannot be undone."
      />
    </div>
  );
}
